import { useMemo, type ReactNode } from 'react';
import { normalizeRenderedQuote } from '@repo/shared-types';
import type { Components } from 'react-markdown';
import type { KbCitation } from '../../lib/api';
import {
  childrenToText, Markdown, MdBlockquote, MdListItem, MdParagraph, MdTableCell,
} from '../ui/markdown';

/**
 * Renders a node's markdown with Wikipedia-style clickable reference markers.
 *
 * The markers are **derived at render time from each citation's `nodeQuote`**, never stored in the
 * markdown. That's what makes inline references safe here: a KB update rewrites the entire body,
 * so markers written into the prose would have to survive every rewrite intact and would decay
 * into references to claims the text no longer makes. A marker recomputed from a quote simply
 * stops appearing the moment its sentence is rewritten — the same signal that marks the citation
 * superseded. The stored markdown stays clean for export, copy-paste and MCP consumers.
 *
 * Injection happens through react-markdown's `components` map rather than by walking the rendered
 * DOM. Markers are then part of the React tree, so a re-render can't strip them and React never
 * has to reconcile against nodes appended behind its back.
 *
 * Markers attach to the *block* containing the quote rather than splicing into the sentence
 * itself: a quote routinely spans `<strong>` or a link, and a marker at the end of the paragraph
 * reads the way footnotes normally do anyway.
 */
export function CitedMarkdown({
  body, citations, onCitationClick,
}: {
  body: string;
  /** Current citations only — superseded ones have no sentence left to attach to. */
  citations: KbCitation[];
  onCitationClick: (citation: KbCitation) => void;
}) {
  const components = useMemo<Components>(() => {
    if (citations.length === 0) return {};

    // Normalized once, not per block per render.
    const needles = citations.map((citation, index) => ({
      citation,
      index: index + 1,
      needle: normalizeRenderedQuote(citation.nodeQuote),
    }));

    const withMarkers = (children: ReactNode): ReactNode => {
      // `children` is already-rendered content, so its text carries no markdown syntax. The quote
      // is verbatim *source* and does, which is why both sides go through normalizeRenderedQuote —
      // matching them raw silently drops every citation containing bold or a code span.
      const text = normalizeRenderedQuote(childrenToText(children));
      if (!text) return children;

      const hits = needles.filter((n) => n.needle && text.includes(n.needle));
      if (hits.length === 0) return children;

      return (
        <>
          {children}
          {hits.map(({ citation, index }) => (
            <button
              key={citation.id}
              type="button"
              className="kb-cite-marker"
              title={citation.claim}
              aria-label={`Citation ${index}: ${citation.claim}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCitationClick(citation);
              }}
            >
              {index}
            </button>
          ))}
        </>
      );
    };

    // Only the block elements that hold prose. Wrapping every element would put a marker on the
    // <strong> inside a paragraph as well as on the paragraph itself.
    return {
      p: ({ children }) => <MdParagraph>{withMarkers(children)}</MdParagraph>,
      li: ({ children }) => <MdListItem>{withMarkers(children)}</MdListItem>,
      td: ({ children, style }) => <MdTableCell style={style}>{withMarkers(children)}</MdTableCell>,
      blockquote: ({ children }) => <MdBlockquote>{withMarkers(children)}</MdBlockquote>,
    };
  }, [citations, onCitationClick]);

  return <Markdown components={components}>{body}</Markdown>;
}
